import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner"; // Import loader
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle, faEnvelope, faPhone, faInfoCircle, faCalendarAlt, faIdBadge } from "@fortawesome/free-solid-svg-icons"; // Icons for profile fields
import { StandardURL } from "../App";

const Profile = () => {
  const [usersData, setUsersData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const userDataString = localStorage.getItem("user");
        const userData = JSON.parse(userDataString);
        const userId = userData.unique;
        const token = localStorage.getItem("token");

        if (!userId || !token) {
          setError("User not logged in or token missing.");
          setLoading(false);
          toast.error("Please log in to view your profile.");
          return;
        }

        // Fetch user data from the endpoint
        const response = await axios.get(
          `${StandardURL}/iNyumba/users/${userId}/edit`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Set the user data state
        setUsersData(response.data.response);
        setLoading(false);
      } catch (error) {
        // Handle errors
        console.error("Error fetching user data:", error);
        setError(error.message || "Failed to load profile data.");
        setLoading(false);
        toast.error("Failed to load profile data. Please try again.");
      }
    };

    // Call the fetchUsersData function when the component mounts
    fetchUsersData();
  }, []);

  // Render loading state while fetching data
  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <Oval
            height={50}
            width={50}
            color="#4F46E5" // Indigo-600
            wrapperStyle={{}}
            wrapperClass=""
            visible={true}
            ariaLabel='oval-loading'
            secondaryColor="#A5B4FC" // Indigo-200
            strokeWidth={4}
            strokeWidthSecondary={4}
          />
          <p className="ml-3 text-lg text-gray-700">Loading profile...</p>
        </div>
      </AuthGuard>
    );
  }

  // Render error message if there's an error
  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-4">Error!</h2>
            <p className="text-gray-700">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
            >
              Try Again
            </button>
          </div>
        </div>
      </AuthGuard>
    );
  }

  // Render the profile data
  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
          <div className="flex items-center justify-between mb-8 border-b pb-4">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800">
              <FontAwesomeIcon icon={faUserCircle} className="mr-3 text-blue-600" />
              User Profile
            </h1>
            {/* Optional: Add an edit button here */}
            {/* <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300">
              Edit Profile
            </button> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faIdBadge} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Name:</strong> {usersData.name}
              </p>
            </div>
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faEnvelope} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Email:</strong> {usersData.email}
              </p>
            </div>
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faPhone} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Phone:</strong> {usersData.phone}
              </p>
            </div>
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faInfoCircle} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Status:</strong>{" "}
                <span className={`font-bold ${usersData.status === 'ACTIVE' ? 'text-green-600' : 'text-red-600'}`}>
                  {usersData.status}
                </span>
              </p>
            </div>
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faIdBadge} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Access Role:</strong>{" "}
                <span className="font-bold text-purple-600">
                  {usersData.role}
                </span>
              </p>
            </div>
            <div className="flex items-center text-gray-700">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-xl text-blue-500 mr-3" />
              <p className="text-lg">
                <strong className="font-semibold text-gray-800">Date Of Joining:</strong>{" "}
                {usersData.created_at ? new Date(usersData.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
             <div className="text-center">
          <p className="mt-2 text-sm text-gray-600">
            Want to change your password?{" "}
            <a
              href="/admin/changepassword"
              className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
            >
              Change Password
            </a>
          </p>
        </div>
          </div>
        </div>
      </div>
    </AuthGuard>
  );
};

export default Profile;